import * as React from 'react';
import PropsChecker from '@telia-front/props-checker';
export const GridCollapseContext = React.createContext({
    gridStyle: {},
    initialColumnCount: 0,
    actualColumnCount: 0,
    isOpen: false,
    isTriggerVisible: true,
});
const GridCollapseProvider = (props) => {
    return (React.createElement(PropsChecker, { componentProps: props, componentName: "GridCollapseProvider", defaultValueName: "defaultOpen", valueName: "isOpen", onChangeNames: "onToggle" },
        React.createElement(GridCollapseProviderInner, Object.assign({}, props))));
};
export default GridCollapseProvider;
const GridCollapseProviderInner = (props) => {
    const { isOpen: outerIsOpen, onToggle, defaultOpen, initialColumnCount } = props;
    const firstUpdate = React.useRef(true);
    const gridRef = React.useRef(null);
    const [gridStyle, setGridStyle] = React.useState({});
    const [innerIsOpen, setInnerIsOpen] = React.useState(defaultOpen || false);
    const [actualColumnCount, setActualColumnCount] = React.useState(0);
    const [initialHeight, setInitialHeight] = React.useState('');
    const [animationState, setAnimationState] = React.useState('closed');
    const isOpen = React.useMemo(() => (onToggle && typeof outerIsOpen !== 'undefined' ? outerIsOpen : innerIsOpen), [innerIsOpen, outerIsOpen, onToggle]);
    const toggle = React.useCallback(() => {
        const open = !isOpen;
        /* istanbul ignore else */
        if (gridRef.current) {
            if (typeof outerIsOpen === 'undefined') {
                setInnerIsOpen(open);
            }
            if (onToggle) {
                onToggle(open);
            }
        }
    }, [isOpen, outerIsOpen, onToggle]);
    /**
     * Get initial height to animate collapse
     */
    React.useEffect(() => {
        /* istanbul ignore else */
        if (gridRef.current) {
            setInitialHeight(`${gridRef.current.scrollHeight}px`);
        }
    }, [gridRef]);
    /**
     * Effect to start animating (triggered with toggle fn).
     * And set starting height for collapse animation.
     */
    React.useLayoutEffect(() => {
        const shouldAnimateonFirstUpdate = !(firstUpdate.current && !isOpen);
        if (gridRef.current && shouldAnimateonFirstUpdate) {
            setAnimationState('animating');
            setGridStyle({
                height: `${gridRef.current.scrollHeight}px`,
                overflow: 'hidden',
            });
        }
        firstUpdate.current = false;
    }, [isOpen, gridRef]);
    /**
     * Effect to animate (triggered with animating state change).
     * Add final Grid styles to achive needed height.
     */
    React.useLayoutEffect(() => {
        if (animationState === 'animating') {
            window.setTimeout(() => {
                /* istanbul ignore else */
                if (gridRef.current) {
                    setGridStyle({
                        height: !isOpen ? initialHeight : `${gridRef.current.scrollHeight}px`,
                        overflow: 'hidden',
                    });
                }
            });
        }
    }, [animationState, isOpen, gridRef, initialHeight]);
    /**
     * Called when final grid height is achieved.
     * Remove grid styles and end animation.
     */
    const setAnimated = () => {
        setAnimationState(isOpen ? 'open' : 'closed');
        setGridStyle({});
    };
    /**
     * Effect to check if initialHeight is changed.
     * Update if needed.
     */
    React.useEffect(() => {
        const scrollHeight = gridRef.current && `${gridRef.current.scrollHeight}px`;
        if (animationState === 'closed' && scrollHeight && scrollHeight !== initialHeight) {
            setInitialHeight(scrollHeight);
        }
    }, [animationState, initialHeight]);
    const gridCollapseContextValue = {
        gridRef,
        gridStyle,
        initialColumnCount,
        actualColumnCount,
        isTriggerVisible: actualColumnCount > initialColumnCount,
        isOpen: animationState !== 'closed',
        setAnimated,
        setActualColumnCount,
        toggle,
    };
    return React.createElement(GridCollapseContext.Provider, { value: gridCollapseContextValue }, props.children);
};
